import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Eye, EyeOff } from "lucide-react";
import { handleChangePassword, logout } from "../../redux/actions/grievanceActions1";
import { DashboardHeader } from "../common/DashboardComponents";
import md5 from "md5";
import { useNavigate } from "react-router-dom";
import {
  validateStrongPassword,
  PASSWORD_RULES_HELP,
} from "../../utils/passwordUtils";

const ChangePassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validCurrentPassword, setValidCurrentPassword] = useState("");
  const [validNewPassword, setValidNewPassword] = useState("");
  const [validConfirmPassword, setValidConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation function
  const validateForm = () => {
    let isValid = true;

    // Reset all error messages
    setValidCurrentPassword("");
    setValidNewPassword("");
    setValidConfirmPassword("");
    setMessage("");

    // Current password validation (existence only — may be legacy weak password)
    if (!currentPassword.trim()) {
      setValidCurrentPassword("Old password is required");
      isValid = false;
    }

    // New password — strong policy
    if (!newPassword.trim()) {
      setValidNewPassword("New password is required");
      isValid = false;
    } else {
      const strength = validateStrongPassword(newPassword);
      if (!strength.isValid) {
        setValidNewPassword(strength.message);
        isValid = false;
      } else if (newPassword === currentPassword) {
        setValidNewPassword(
          "New password must be different from current password"
        );
        isValid = false;
      }
    }

    // Confirm password validation
    if (!confirmPassword.trim()) {
      setValidConfirmPassword("Please confirm your password");
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      setValidConfirmPassword("Passwords do not match");
      isValid = false;
    }

    return isValid;
  };

  // Reset form function
  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setValidCurrentPassword("");
    setValidNewPassword("");
    setValidConfirmPassword("");
    setMessage("");
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));

      const eventData = {
        addInfo: {
          user_id: storedUser.id,
          role_id: storedUser.role,
          old_password: md5(currentPassword.trim()),
          new_password: md5(newPassword.trim()),
        },
      };
     

      const res = await dispatch(handleChangePassword(eventData));
      setIsSubmitting(false);
      if (res.rCode === 0) {
        alert(
          res.rMessage ||
            "Password changed successfully! Please log in again. Other sessions have been signed out."
        );
        resetForm();
        // Broadcast so other same-browser tabs clear session (CWE-613)
        dispatch(logout({ broadcast: true }));
        navigate("/", { replace: true });
      } else {
        alert(res.rMessage || " Error in Password changed!");
        resetForm();
      }
    } catch (error) {
      console.error("Error changing password:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes with validation reset
  const handleCurrentPasswordChange = (e) => {
    setCurrentPassword(e.target.value);
    if (validCurrentPassword) setValidCurrentPassword("");
    if (message) setMessage("");
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    if (validNewPassword) setValidNewPassword("");
    if (
      validConfirmPassword &&
      confirmPassword &&
      e.target.value === confirmPassword
    ) {
      setValidConfirmPassword("");
    }
    if (message) setMessage("");
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (validConfirmPassword) setValidConfirmPassword("");
    if (message) setMessage("");
  };

  return (
    <div className="container-fluid p-4">
      <DashboardHeader title="Change Password" />

      <div id="layout" className="theme-cyan mt-3">
        <div id="wrapper">
          <div id="main-content">
            <div className="">
              <div className="row g-3">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="card-title">Update Password</h6>
                    </div>
                    <div className="card-body">
                      <form className="row g-3" onSubmit={handleUpdate}>
                        {/* Old Password */}
                        <div className="col-md-6 col-12 position-relative">
                          <label className="form-label">
                            Old Password <span className="text-danger">*</span>
                          </label>
                          <div className="position-relative">
                            <input
                              type={showCurrentPassword ? "text" : "password"}
                              className={`form-control ${
                                validCurrentPassword ? "is-invalid" : ""
                              }`}
                              value={currentPassword}
                              onChange={handleCurrentPasswordChange}
                              placeholder="Enter old password"
                              disabled={isSubmitting}
                            />
                            <button
                              type="button"
                              className="btn position-absolute"
                              style={{
                                top: "50%",
                                right: "10px",
                                transform: "translateY(-50%)",
                                border: "none",
                                background: "transparent",
                                padding: "0",
                                cursor: "pointer",
                                zIndex: 10,
                              }}
                              onClick={() =>
                                setShowCurrentPassword(!showCurrentPassword)
                              }
                              disabled={isSubmitting}
                              aria-label="Toggle password visibility"
                            >
                              {showCurrentPassword ? (
                                <EyeOff size={18} className="text-muted" />
                              ) : (
                                <Eye size={18} className="text-muted" />
                              )}
                            </button>
                          </div>
                          {validCurrentPassword && (
                            <div className="invalid-feedback d-block">
                              {validCurrentPassword}
                            </div>
                          )}
                        </div>

                        {/* New Password */}
                        <div className="col-md-6 col-12 position-relative">
                          <label className="form-label">
                            New Password <span className="text-danger">*</span>
                          </label>
                          <div className="position-relative">
                            <input
                              type={showNewPassword ? "text" : "password"}
                              className={`form-control ${
                                validNewPassword ? "is-invalid" : ""
                              }`}
                              value={newPassword}
                              onChange={handleNewPasswordChange}
                              placeholder="Enter new password"
                              disabled={isSubmitting}
                            />
                            <button
                              type="button"
                              className="btn position-absolute"
                              style={{
                                top: "50%",
                                right: "10px",
                                transform: "translateY(-50%)",
                                border: "none",
                                background: "transparent",
                                padding: "0",
                                cursor: "pointer",
                                zIndex: 10,
                              }}
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              disabled={isSubmitting}
                              aria-label="Toggle password visibility"
                            >
                              {showNewPassword ? (
                                <EyeOff size={18} className="text-muted" />
                              ) : (
                                <Eye size={18} className="text-muted" />
                              )}
                            </button>
                          </div>
                          {validNewPassword && (
                            <div className="invalid-feedback d-block">
                              {validNewPassword}
                            </div>
                          )}
                          <div className="form-text">{PASSWORD_RULES_HELP}</div>
                        </div>

                        {/* Confirm Password */}
                        <div className="col-md-6 col-12 position-relative">
                          <label className="form-label">
                            Confirm Password{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="position-relative">
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              className={`form-control ${
                                validConfirmPassword ? "is-invalid" : ""
                              }`}
                              value={confirmPassword}
                              onChange={handleConfirmPasswordChange}
                              placeholder="Confirm new password"
                              disabled={isSubmitting}
                            />
                            <button
                              type="button"
                              className="btn position-absolute"
                              style={{
                                top: "50%",
                                right: "10px",
                                transform: "translateY(-50%)",
                                border: "none",
                                background: "transparent",
                                padding: "0",
                                cursor: "pointer",
                                zIndex: 10,
                              }}
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              disabled={isSubmitting}
                              aria-label="Toggle password visibility"
                            >
                              {showConfirmPassword ? (
                                <EyeOff size={18} className="text-muted" />
                              ) : (
                                <Eye size={18} className="text-muted" />
                              )}
                            </button>
                          </div>
                          {validConfirmPassword && (
                            <div className="invalid-feedback d-block">
                              {validConfirmPassword}
                            </div>
                          )}
                        </div>

            

                        {/* Submit Button */}
                        <div className="col-12">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Updating...
                              </>
                            ) : (
                              "Change Password"
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
